import { AlertTriangle, ArrowRight, Building2, Check, CircleSlash, Cpu, FileJson, FolderGit2, Lock, Play, PlugZap, Save, SlidersHorizontal, Sparkles, Waypoints } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import type { EffectiveLaunch, EffectiveSetting, Id, PolicyConfig, PolicyRule, PolicyScope, PolicyState, SettingSource } from '@orgus/protocol';
import { PERMISSION_MODES, layerConflicts } from '@orgus/policy';
import type { LayerConflict } from '@orgus/policy';
import { Badge, Button, EmptyState, ErrorState, Input, Panel, RelativeTime, Segmented, Select, Skeleton, Textarea, cn, toUiError, toast, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, tableClass } from '@orgus/ui';
import type { Tone } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { fetchEffective, saveSettings, usePolicyV2, useSecrets, withLayer } from './live.ts';
import { IDLE_CLOSE_PRESETS, MODE_LABEL, SOURCE_LABEL, describeSource, formatValue, idleCloseLabel, settingsFormOf, settingsOf } from './model.ts';
import { DEFAULT_IDLE_CLOSE_MINUTES } from '@orgus/protocol';
import type { SettingsForm } from './model.ts';

const SOURCE_META: Record<SettingSource, { tone: Tone; icon: ReactNode }> = {
  default: { tone: 'neutral', icon: <Lock /> },
  org: { tone: 'accent', icon: <Building2 /> },
  project: { tone: 'working', icon: <FolderGit2 /> },
  session: { tone: 'waiting', icon: <Play /> },
};

export function SourceBadge({ source }: { source: SettingSource }) {
  return (
    <Badge tone={SOURCE_META[source].tone} icon={SOURCE_META[source].icon} variant={source === 'default' ? 'outline' : 'soft'}>
      {SOURCE_LABEL[source]}
    </Badge>
  );
}

export function useProjects() {
  const projects = useOrgus(useShallow((s) => s.projects));
  return useMemo(() => {
    const list = Object.values(projects).sort((a, b) => a.name.localeCompare(b.name));
    return { list, name: (id: Id) => projects[id]?.name };
  }, [projects]);
}

const NO_RULES: PolicyRule[] = [];

/** Launches a layer refuses, said per project: "Project web: The model opus is not allowed here. ..." */
function ConflictList({ conflicts, name, title }: { conflicts: LayerConflict[]; name: (id: Id) => string | undefined; title: string }) {
  return (
    <div role="status" className="flex items-start gap-2 rounded-md bg-waiting-soft px-3 py-2 text-sm text-fg">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-waiting" aria-hidden />
      <div className="flex flex-col gap-1">
        <span className="font-medium">{title}</span>
        <ul className="flex flex-col gap-0.5">
          {conflicts.flatMap((c) =>
            c.violations.map((v) => (
              <li key={`${c.projectId ?? 'none'}:${v}`}>
                <span className="text-fg-muted">{c.projectId ? (name(c.projectId) ?? c.projectId) : 'Sessions outside a project'}:</span> {v}
              </li>
            )),
          )}
        </ul>
        <span className="text-fg-muted">Change the default model or the permission mode here, or the policy of the layer below.</span>
      </div>
    </div>
  );
}

/** Default, three presets and Never; a stored value off the list stays visible as its own choice. */
function idleOptions(current: string, inheritLabel: string): { value: string; label: string }[] {
  const presets = IDLE_CLOSE_PRESETS.map(String);
  const extra = current !== 'inherit' && !presets.includes(current) ? [current] : [];
  return [{ value: 'inherit', label: inheritLabel }, ...[...presets.filter((p) => p !== String(DEFAULT_IDLE_CLOSE_MINUTES)), ...extra].map((m) => ({ value: m, label: idleCloseLabel(Number(m)) }))];
}

function ViewSkeleton({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label={label}>
      <Skeleton className="h-6 w-64" />
      <Skeleton className="h-[260px]" />
      <Skeleton className="h-[200px]" />
    </div>
  );
}

/** One layer's launch settings (org or a project), with what the layer below gives when a field is left empty. */
function LayerEditor({ config, secretNames, secretsMissing }: { config: PolicyConfig; secretNames: string[]; secretsMissing: boolean }) {
  const { list, name } = useProjects();
  const [scope, setScope] = useState('org');
  const layer = scope === 'org' ? config.org : config.projects[scope];
  const [form, setForm] = useState<SettingsForm>(() => settingsFormOf(layer));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rules = useOrgus((s) => (s.ext.policy as PolicyState | undefined)?.rules) ?? NO_RULES;
  const scopeOf = (): PolicyScope => (scope === 'org' ? { kind: 'org' } : { kind: 'project', projectId: scope });
  // What saving this form would refuse, before it is saved.
  const conflicts = useMemo(() => {
    const v = settingsOf(form);
    return v.ok ? layerConflicts(rules, withLayer(config, scopeOf(), v.settings), scopeOf()) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, config, rules, scope]);
  const layerKey = JSON.stringify(layer ?? {});
  useEffect(() => {
    setForm(settingsFormOf(layer));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, layerKey]);
  const set = <K extends keyof SettingsForm>(k: K, v: SettingsForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const inherited = scope === 'org' ? null : config.org;
  const from = (v: unknown, key: EffectiveSetting['key']) => (inherited ? `Empty: ${formatValue(key, v)} from Every project.` : undefined);

  const save = async () => {
    const v = settingsOf(form);
    if (!v.ok) return setError(v.error);
    setBusy(true);
    setError(null);
    try {
      // Landing keys are edited in Landing and freezes; keep them as they are.
      const keep = { ...(layer?.requiredChecks ? { requiredChecks: layer.requiredChecks } : {}), ...(layer?.reviewer ? { reviewer: layer.reviewer } : {}) };
      const { requiredChecks: _r, reviewer: _v, ...launch } = v.settings;
      const warnings = await saveSettings(scopeOf(), { ...launch, ...keep });
      const refused = warnings.reduce((n, w) => n + w.violations.length, 0);
      if (refused) toast('Launch settings saved, with conflicts', { tone: 'error', description: `${refused === 1 ? 'One check refuses' : `${refused} checks refuse`} new sessions until the layers agree.` });
      else toast('Launch settings saved', { tone: 'success', description: 'Running sessions keep their old settings.' });
    } catch (e) {
      const u = toUiError(e);
      setError(`${u.what} ${u.why ?? ''} ${u.next ?? ''}`.replace(/\s+/g, ' ').trim());
    } finally {
      setBusy(false);
    }
  };

  const toggleSecret = (n: string) => {
    const cur = form.secrets ?? [];
    set('secrets', cur.includes(n) ? cur.filter((x) => x !== n) : [...cur, n]);
  };
  const chosen = form.secrets ?? [];
  const allNames = [...new Set([...secretNames, ...chosen])].sort();

  return (
    <Panel
      title="Settings by layer"
      sub={scope === 'org' ? 'every project' : (name(scope) ?? 'project')}
      icon={<SlidersHorizontal />}
      actions={
        <Select
          aria-label="Layer"
          size="sm"
          value={scope}
          onValueChange={setScope}
          options={[{ value: 'org', label: 'Every project' }, ...list.map((p) => ({ value: p.id, label: `${p.name}${config.projects[p.id] ? '' : ' (inherits)'}` }))]}
        />
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="Default model" value={form.defaultModel} onChange={(e) => set('defaultModel', e.target.value)} placeholder={inherited?.defaultModel ?? 'Claude Code default'} hint={from(inherited?.defaultModel, 'defaultModel') ?? 'Used when the person starting a session picks none.'} spellCheck={false} className="font-mono" />
          <Input
            label="Allowed models"
            value={form.allowedModels}
            onChange={(e) => set('allowedModels', e.target.value)}
            placeholder={inherited?.allowedModels?.join(', ') ?? 'Any model'}
            hint={from(inherited?.allowedModels, 'allowedModels') ?? 'Comma separated; sonnet matches claude-sonnet-4-5. Enforced at launch, and switches are recorded.'}
            spellCheck={false}
            className="font-mono"
          />
          <Select
            label="Permission mode"
            value={form.permissionMode}
            onValueChange={(v) => set('permissionMode', v)}
            hint={from(inherited?.permissionMode, 'permissionMode')}
            options={[{ value: 'inherit', label: scope === 'org' ? 'Built in (ask as usual)' : 'Inherit' }, ...PERMISSION_MODES.map((m) => ({ value: m, label: MODE_LABEL[m] ?? m }))]}
          />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-fg-muted">Bypass permissions</span>
            <Segmented<SettingsForm['disableBypassPermissions']>
              label="Bypass permissions"
              value={form.disableBypassPermissions}
              onChange={(v) => set('disableBypassPermissions', v)}
              options={[
                { value: 'inherit', label: 'Inherit' },
                { value: 'yes', label: 'Blocked' },
                { value: 'no', label: 'Allowed' },
              ]}
            />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-muted">Autonomy</span>
            <Segmented<SettingsForm['autonomy']>
              label="Autonomy"
              value={form.autonomy}
              onChange={(v) => set('autonomy', v)}
              options={[
                { value: 'inherit', label: scope === 'org' ? 'Built in' : 'Inherit' },
                { value: 'full', label: 'Full' },
                { value: 'ask', label: 'Ask people' },
              ]}
            />
            <span className="text-sm text-fg-subtle">Full: nothing waits for a person. Your own ask rules still ask; the Orgus folder stays off limits.</span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-fg-muted">Close idle sessions after</span>
            <Segmented<string>
              label="Close idle sessions after"
              value={form.idleClose}
              onChange={(v) => set('idleClose', v)}
              options={idleOptions(form.idleClose, scope === 'org' ? `Default (${idleCloseLabel(DEFAULT_IDLE_CLOSE_MINUTES)})` : `Inherit (${idleCloseLabel(inherited?.idleCloseMinutes ?? DEFAULT_IDLE_CLOSE_MINUTES)})`)}
            />
            <span className="text-sm text-fg-subtle">Closed sessions can be resumed. Saves machines and cost.</span>
          </div>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-sm font-medium text-fg-muted">Secrets leased to each session</legend>
          {scope !== 'org' && (
            <Segmented<'inherit' | 'choose'>
              label="Secrets for this project"
              value={form.secrets ? 'choose' : 'inherit'}
              onChange={(v) => set('secrets', v === 'inherit' ? null : (inherited?.secrets ?? []))}
              options={[
                { value: 'inherit', label: `Inherit (${formatValue('secrets', inherited?.secrets ?? [])})` },
                { value: 'choose', label: 'Choose for this project' },
              ]}
            />
          )}
          {(scope === 'org' || form.secrets) &&
            (allNames.length ? (
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Secrets">
                {allNames.map((n) => {
                  const on = chosen.includes(n);
                  const missing = !secretNames.includes(n);
                  return (
                    <Button key={n} size="sm" variant={on ? 'primary' : 'outline'} aria-pressed={on} icon={on ? <Check /> : undefined} onClick={() => toggleSecret(n)} className="font-mono" title={missing ? 'Not stored on this hub: it will not be leased until it is added in Secrets.' : undefined}>
                      {n}
                      {missing ? ' (not stored)' : ''}
                    </Button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-fg-muted">
                No secrets yet: add one in <Link to="/settings/secrets" className="text-accent-text underline-offset-2 hover:underline">Secrets</Link>.
              </p>
            ))}
          <p className="text-sm text-fg-subtle">
            {secretsMissing ? 'This hub runs without the secrets module, so nothing is leased.' : 'Leased to headless sessions as environment variables, revoked when the session ends, masked in everything Orgus reads.'}
          </p>
        </fieldset>

        <Textarea
          label="Environment"
          rows={3}
          value={form.env}
          onChange={(e) => set('env', e.target.value)}
          placeholder={'CI=1\nLOG_LEVEL=warn'}
          hint="One NAME=value per line, written to a settings file, so no secrets."
          spellCheck={false}
          className="font-mono"
        />
        <button type="submit" hidden />
        {error && (
          <p role="alert" className="rounded-md bg-failed-soft px-3 py-2 text-sm text-failed">
            {error}
          </p>
        )}
        {conflicts.length > 0 && <ConflictList conflicts={conflicts} name={name} title="With these settings, new sessions are refused" />}
        <div className="flex items-center justify-end gap-2">
          <span className="mr-auto text-sm text-fg-subtle">Applies only to sessions Orgus starts.</span>
          <Button variant="primary" icon={<Save />} loading={busy} onClick={() => void save()}>
            Save {scope === 'org' ? 'for every project' : 'for this project'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

/** "Where did this value come from": what a launch in a project gets, layer by layer. */
function WhereFrom({ version }: { version: number }) {
  const { list } = useProjects();
  const [projectId, setProjectId] = useState<string>(list[0]?.id ?? 'none');
  const [model, setModel] = useState('');
  const [mode, setMode] = useState('inherit');
  const [res, setRes] = useState<EffectiveLaunch | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [showJson, setShowJson] = useState(false);

  const load = async () => {
    setError(null);
    try {
      setRes(await fetchEffective(projectId === 'none' ? null : projectId, { ...(model.trim() ? { model: model.trim() } : {}), ...(mode !== 'inherit' ? { permissionMode: mode } : {}) }));
    } catch (e) {
      setError(e);
    }
  };
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, model, mode, version]);

  const rows = res?.settings.filter((s) => !['requiredChecks', 'reviewer'].includes(s.key)) ?? [];
  return (
    <Panel title="Where each value comes from" sub={`policy version ${version}`} icon={<Waypoints />} flush collapsible>
      <div className="grid gap-3 border-b border-border-subtle px-4 py-3 sm:grid-cols-3">
        <Select label="A session in" size="sm" value={projectId} onValueChange={setProjectId} options={[...list.map((p) => ({ value: p.id, label: p.name })), { value: 'none', label: 'A folder in no project' }]} />
        <Input label="Started with model" size="sm" value={model} onChange={(e) => setModel(e.target.value)} placeholder="the default" spellCheck={false} className="font-mono" />
        <Select label="And permission mode" size="sm" value={mode} onValueChange={setMode} options={[{ value: 'inherit', label: 'Not chosen' }, ...PERMISSION_MODES.map((m) => ({ value: m, label: MODE_LABEL[m] ?? m }))]} />
      </div>
      {error ? (
        <div className="p-4">
          <ErrorState error={error} variant="inline" onRetry={load} />
        </div>
      ) : !res ? (
        <div className="flex flex-col gap-2 p-4" role="status" aria-label="Resolving settings">
          <Skeleton className="h-5" />
          <Skeleton className="h-5" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      ) : (
        <>
          {res.violations.length > 0 && (
            <ul className="flex flex-col gap-1 border-b border-border-subtle bg-failed-soft px-4 py-2.5" aria-label="Why this launch would be refused">
              {res.violations.map((v) => (
                <li key={v} className="flex items-start gap-2 text-sm text-failed">
                  <CircleSlash className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span>Refused at launch: {v}</span>
                </li>
              ))}
            </ul>
          )}
          <Table aria-label="Settings a launched session gets and where each one came from">
            <TableHeader>
              <TableRow>
                <TableHead>Setting</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>From</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={`${s.key}:${s.label}`}>
                  <th scope="row" className={cn(tableClass.cell, tableClass.name, 'whitespace-nowrap py-2 pl-row-x pr-3 text-left')}>
                    {s.key === 'env' ? <span className="font-mono">{s.label}</span> : s.label}
                  </th>
                  <TableCell className="whitespace-normal py-2">
                    <span className="font-mono">{formatValue(s.key, s.value)}</span>
                    {s.overridden.length > 0 && (
                      <span className="ml-2 text-fg-subtle" title={describeSource(s)}>
                        replaces <span className="line-through">{formatValue(s.key, s.overridden[0]!.value)}</span>
                      </span>
                    )}
                    {s.note && <span className="block text-caption text-fg-subtle">{s.note}</span>}
                  </TableCell>
                  <TableCell>
                    <SourceBadge source={s.source} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="border-t border-border-subtle px-4 py-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium text-fg">
              <Sparkles className="size-3.5 text-fg-subtle" aria-hidden /> Permission rules passed to Claude Code
            </h4>
            {res.permissions.length === 0 ? (
              <p className="text-sm text-fg-muted">None: the approval hooks decide every tool call.</p>
            ) : (
              <ul className="flex flex-col gap-1" aria-label="Generated permission rules">
                {res.permissions.map((p) => (
                  <li key={`${p.list}:${p.rule}`} className="flex items-center gap-2 text-sm">
                    <Badge tone={p.list === 'allow' ? 'passed' : p.list === 'deny' ? 'failed' : 'waiting'} icon={p.list === 'allow' ? <Check /> : p.list === 'deny' ? <CircleSlash /> : <AlertTriangle />}>
                      {p.list === 'allow' ? 'Allow' : p.list === 'deny' ? 'Deny' : 'Ask'}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate font-mono text-fg">{p.rule}</span>
                    <SourceBadge source={p.source} />
                  </li>
                ))}
              </ul>
            )}
            {res.hookOnly.length > 0 && <p className="mt-2 text-sm text-fg-subtle">{res.hookOnly.length} more {res.hookOnly.length === 1 ? 'rule is' : 'rules are'} applied by the hook only, because Claude Code's rule syntax cannot express them the same way.</p>}
            <p className="mt-2 text-sm text-fg-subtle">Secrets: {res.secrets.length ? res.secrets.join(', ') : 'none'}. Values never appear here.</p>
            <Button size="sm" variant="ghost" icon={<FileJson />} className="mt-2" aria-expanded={showJson} onClick={() => setShowJson((v) => !v)}>
              {showJson ? 'Hide the settings file' : 'Show the settings file'}
            </Button>
            {showJson && <pre className="mt-2 max-h-64 overflow-auto rounded-md border border-border-subtle bg-raised p-3 font-mono text-xs text-fg">{JSON.stringify(res.claude, null, 2)}</pre>}
          </div>
        </>
      )}
    </Panel>
  );
}

function ModelSwitches() {
  const { policy } = usePolicyV2();
  const sessions = useOrgus((s) => s.sessions);
  const list = policy?.modelSwitches ?? [];
  return (
    <Panel title="Model switches" sub={list.length ? `${list.length} recent` : 'None yet'} icon={<Cpu />} flush collapsible>
      {list.length === 0 ? (
        <EmptyState icon={<Cpu />} title="No model switches yet" description="Model changes show up here, checked against the allowed models." />
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Model switches, newest first">
          {list.map((m) => (
            <li key={`${m.sessionId}:${m.at}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
              <Badge tone={m.allowed ? 'passed' : 'failed'} icon={m.allowed ? <Check /> : <AlertTriangle />}>
                {m.allowed ? 'Allowed' : 'Not allowed'}
              </Badge>
              <span className="flex items-center gap-1.5 font-mono text-fg">
                {m.from ?? 'unknown'} <ArrowRight className="size-3 text-fg-subtle" aria-label="to" /> {m.to}
              </span>
              <span className="min-w-0 flex-1 truncate text-fg-muted" title={m.reason}>
                {sessions[m.sessionId]?.title ?? m.sessionId}
                {m.launched ? '' : ' (watched)'}
              </span>
              <RelativeTime ts={m.at} className="shrink-0 text-fg-subtle" />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Settings, Launch settings: what sessions Orgus starts get, and where each value comes from. */
export function LaunchSettingsView() {
  const { phase, policy, config } = usePolicyV2();
  const secrets = useSecrets();
  const loadError = useOrgus((s) => s.loadError);
  if (phase === 'loading') return <ViewSkeleton label="Loading launch settings" />;
  if (phase === 'error') return <ErrorState error={loadError ?? new Error('The hub could not be reached.')} />;
  if (phase === 'missing' || !policy) {
    return <EmptyState icon={<PlugZap />} title="Launch settings are not available on this hub" description="Update Orgus on the hub machine, then restart it." />;
  }
  return (
    <div className={cn('flex max-w-[960px] flex-col gap-6')}>
      <LayerEditor config={config} secretNames={secrets.secrets?.secrets.map((s) => s.name) ?? []} secretsMissing={secrets.phase === 'missing'} />
      <WhereFrom version={policy.version} />
      <ModelSwitches />
    </div>
  );
}
